import { lazy } from 'react';

import { Navigate } from 'react-router';

import { DefaultRouteWithParam } from '@/components/DefaultRouteWithParam';
import { BITSTOP_OPERATOR_ID } from '@/constants/app/config';

export const POPOVER_ACTIONS = ['/settings/integrations/blockchain-analytics'];

// We want to lazy load the pages for better performance
const Machine = lazy(() => import('@/pages/machines/Machine'));
const Machines = lazy(() => import('@/pages/machines/Machines'));
const Customer = lazy(() => import('@/pages/customers/Customer'));
const Customers = lazy(() => import('@/pages/customers/Customers'));
const Transactions = lazy(() => import('@/pages/transactions/Transactions'));

const ProvisioningQrCode = lazy(() => import('@/pages/settings/maintenance/ProvisioningQrCode'));
const BlockchainAnalytics = lazy(() => import('@/pages/settings/integrations/BlockchainAnalytics'));

const Wallet = lazy(() => import('@/pages/wallet/Wallet'));
const Wallets = lazy(() => import('@/pages/wallets/Wallets'));

const Compliance = lazy(() => import('@/pages/compliance/Compliance'));

const Invoices = lazy(() => import('@/pages/invoices/Invoices'));
const Invoice = lazy(() => import('@/pages/invoices/invoice'));

const Stats = lazy(() => import('@/pages/stats/Stats'));

const Users = lazy(() => import('@/pages/users/Users'));
const UsersV2 = lazy(() => import('@/pages/users/UsersV2'));

const Affiliates = lazy(() => import('@/pages/affiliates/Affiliates'));
const Affiliate = lazy(() => import('@/pages/affiliates/Affiliate'));

const Dashboard = lazy(() => import('@/pages/Dashboard'));

const Locations = lazy(() => import('@/pages/locations/Locations'));
const Location = lazy(() => import('@/pages/locations/Location'));

// const Listings = lazy(() => import('@/pages/listings/Listings'));

const Issues = lazy(() => import('@/pages/issues/Issues'));

const Cases = lazy(() => import('@/pages/cases/Cases'));
const CaseCreate = lazy(() => import('@/pages/cases/CaseCreate'));
const Case = lazy(() => import('@/pages/cases/Case'));

const ServiceTickets = lazy(() => import('@/pages/serviceTickets/ServiceTickets'));
const ServiceTicket = lazy(() => import('@/pages/serviceTickets/ServiceTicket'));
const ServiceTicketCreate = lazy(() => import('@/pages/serviceTickets/ServiceTicketCreate'));

const Routes = lazy(() => import('@/pages/routes/Routes'));
const Route = lazy(() => import('@/pages/routes/Route'));
const RouteCreate = lazy(() => import('@/pages/routes/RouteCreate'));
const Merchants = lazy(() => import('@/pages/merchants/Merchants'));
const Merchant = lazy(() => import('@/pages/merchants/Merchant'));
const MerchantCreate = lazy(() => import('@/pages/merchants/MerchantCreate'));
const Restocking = lazy(() => import('@/pages/restocking/Restocking'));
const LoadOrder = lazy(() => import('@/pages/restocking/LoadOrder'));
const CreateLoadOrder = lazy(() => import('@/pages/restocking/CreateLoadOrder'));
const Configs = lazy(() => import('@/pages/configs/Configs'));
const Listings = lazy(() => import('@/pages/marketing/Listings'));
const Listing = lazy(() => import('@/pages/marketing/Listing'));
const MarketingDashboard = lazy(() => import('@/pages/marketing/MarketingDashboard'));
const Coupons = lazy(() => import('@/pages/marketing/Coupons'));
const Coupon = lazy(() => import('@/pages/marketing/Coupon'));
const CashLogs = lazy(() => import('@/pages/cashLogs'));
const CashLog = lazy(() => import('@/pages/cashLog'));
const Sale = lazy(() => import('@/pages/sales/sale/Sale'));

/**
 * Settings routes configuration
 * * Settings has his own configuration, this is because we are using a different layout for those routes
 */
export const SETTINGS_ROUTES = [
  {
    path: '/settings/integrations/blockchain-analytics',
    element: <BlockchainAnalytics />,
    title: null,
  },
  {
    path: '/settings/integrations',
    element: <Navigate to="/settings/integrations/blockchain-analytics" replace />,
  },
  {
    path: '/settings/maintenance/provisioning-qr-code',
    element: <ProvisioningQrCode />,
    title: null,
  },
  {
    path: '/settings/maintenance',
    element: <Navigate to="/settings/integrations/blockchain-analytics" replace />,
  },
  {
    path: '/settings',
    element: <Navigate to="/settings/integrations/blockchain-analytics" replace />,
  },
];

/**
 * Main routes configuration
 * * The routes visibility can be configured here
 * ! If you want to hide or show an existing view, please remember to update the sidebar menu configuration (sidebar.ts)
 */
export const MAIN_ROUTES = [
  // Dashboard views
  { path: '/', element: <Dashboard />, title: 'Dashboard' },

  // Transactions Views
  { path: '/sales/transactions', element: <Transactions />, title: 'Sales' },
  { path: '/sales/transactions/:saleId', element: <Sale />, title: 'Sale' },
  { path: '/sales/transactions/:saleId/status-log', element: <Sale />, title: 'Sale' },
  { path: '/sales/transactions/:saleId/dispense', element: <Sale />, title: 'Sale' },
  { path: '/sales/transactions/:saleId/redemption', element: <Sale />, title: 'Sale' },
  { path: '/sales/batching', element: <Transactions />, title: 'Sales' },
  { path: '/sales', element: <Navigate to="/sales/transactions" replace /> },

  // Machines Views
  { path: '/machines/atms', element: <Machines />, title: 'Machines' },
  { path: '/machines/cash-logs', element: <Machines />, title: 'Machines' },
  { path: '/machines/integrations', element: <Machines />, title: 'Machines' },
  { path: '/machine/:id', element: <Machine /> },
  { path: '/machine/:id/sales', element: <Machine /> },
  { path: '/machine/:id/sales/:saleId', element: <Machine /> },
  { path: '/machine/:id/products', element: <Machine /> },
  { path: '/machine/:id/cashbox', element: <Machine /> },
  { path: '/machine/:id/cash-logs', element: <Machine /> },
  { path: '/machine/:id/inventory', element: <Machine /> },
  { path: '/machine/:id/monitoring', element: <Machine /> },
  { path: '/machines', element: <Navigate to="/machines/atms" replace /> },

  // Cash Logs Views
  { path: '/cash-logs', element: <CashLogs />, title: 'Cash Logs' },
  { path: '/cash-log/:id', element: <CashLog />, title: 'Cash Log' },

  {
    path: '/location/:id',
    //element: <Navigate to="/location/:id/details" replace />,
    element: <Location />,
  },
  { path: '/location/:id/details', element: <Location /> },
  { path: '/location/:id/notes', element: <Location /> },
  { path: '/location/:id/sales', element: <Location /> },
  // { path: '/location/:id/settings', element: <Location />},
  { path: '/locations', element: <Locations />, title: 'Locations' },
  { path: '/locations/unassigned-locations', element: <Locations />, title: 'Locations' },

  // Restocking
  { path: '/restocking', element: <Restocking /> },
  { path: '/restocking/load-orders', element: <Restocking /> },
  { path: '/restocking/load-orders/new', element: <CreateLoadOrder /> },
  { path: '/restocking/load-orders/:id', element: <LoadOrder /> },
  { path: '/restocking/load-orders/:id/inventory', element: <LoadOrder /> },

  // Wallets Views
  { path: '/wallets/:provider/:coin/activity', element: <Wallets /> },
  { path: '/wallets/:provider/:coin/funding', element: <Wallets /> },
  { path: '/wallets/:provider/:coin/settings', element: <Wallets /> },
  { path: '/wallets', element: <Wallets /> },

  { path: '/wallet/transactions', element: <Wallet />, title: 'Wallet' },
  { path: '/wallet/addresses', element: <Wallet />, title: 'Wallet' },
  { path: '/wallet', element: <Navigate to="/wallet/transactions" replace /> },

  // Compliance Views
  { path: '/compliance', element: <Compliance />, title: 'Compliance' },

  // Invoices Views
  { path: '/invoices', element: <Invoices />, title: 'Invoices' },
  { path: '/invoice/:id', element: <Invoice /> },

  // Stats Views
  { path: '/stats', element: <Stats />, title: 'Stats' },

  // Users Views
  { path: '/users', element: <Users />, title: 'Users' },
  { path: '/users-v2', element: <UsersV2 />, title: 'Users' },

  // Affiliates Views
  { path: '/affiliates', element: <Affiliates />, title: 'Affiliates' },
  { path: '/affiliate/:id/users', element: <Affiliate /> },
  { path: '/affiliate/:id/machines', element: <Affiliate /> },
  { path: '/affiliate/:id/sales', element: <Affiliate /> },
  {
    path: '/affiliate/:id',
    element: <DefaultRouteWithParam path="/affiliate/:id/users" />,
  },

  // Customers
  { path: '/customers', element: <Customers />, title: 'Customers' },
  { path: '/customer/:id/sales', element: <Customer /> },
  { path: '/customer/:id/kyc', element: <Customer /> },
  { path: '/customer/:id/manual-verification', element: <Customer /> },
  { path: '/customer/:id/notes', element: <Customer /> },
  { path: '/customer/:id/linked-accounts', element: <Customer /> },
  { path: '/customer/:id/potential-matches', element: <Customer /> },
  {
    path: '/customer/:id',
    element: <DefaultRouteWithParam path="/customer/:id/sales" />,
  },

  // Operations Views
  { path: '/merchants', element: <Merchants /> },
  { path: '/merchants/unassigned-locations', element: <Merchants /> },
  { path: '/merchants/create', element: <MerchantCreate /> },
  { path: '/merchant/:id/details', element: <Merchant /> },
  { path: '/merchant/:id/locations', element: <Merchant /> },
  {
    path: '/merchant/:id',
    element: <DefaultRouteWithParam path="/merchant/:id/details" />,
  },
  { path: '/issues', element: <Issues /> },

  { path: '/cases', element: <Cases /> },
  { path: '/cases/create', element: <CaseCreate /> },
  { path: '/case/:id', element: <Case /> },
  { path: '/case/:id/issues', element: <Case /> },
  { path: '/case/:id/related-service-tickets', element: <Case /> },
  { path: '/case/:id/location', element: <Case /> },
  { path: '/case/:id/machine', element: <Case /> },
  { path: '/case/:id/events', element: <Case /> },
  { path: '/case/:id/cash-box', element: <Case /> },
  { path: '/case/:id/cash-logs', element: <Case /> },
  { path: '/case/:id/notes', element: <Case /> },

  { path: '/service-tickets', element: <ServiceTickets /> },
  { path: '/service-ticket/create', element: <ServiceTicketCreate /> },
  { path: '/service-ticket/:id', element: <ServiceTicket /> },
  { path: '/service-ticket/:id/details', element: <ServiceTicket /> },
  { path: '/service-ticket/:id/tasks', element: <ServiceTicket /> },
  { path: '/service-ticket/:id/related-case', element: <ServiceTicket /> },
  { path: '/service-ticket/:id/machine-details', element: <ServiceTicket /> },
  { path: '/service-ticket/:id/machine-events', element: <ServiceTicket /> },
  { path: '/service-ticket/:id/notes', element: <ServiceTicket /> },

  // Routes Views
  { path: '/routes', element: <Routes /> },
  { path: '/routes/create', element: <RouteCreate /> },
  { path: '/route/:id', element: <Route /> },

  // Configs Views
  { path: '/configs', element: <Configs /> },
  { path: '/configs/resolution-reasons', element: <Configs /> },
  { path: '/configs/ops-services', element: <Configs /> },
  { path: '/configs/cash-services', element: <Configs /> },
  { path: '/configs/cash-vault-branches', element: <Configs /> },

  // Marketing Views
  { path: '/marketing', element: <MarketingDashboard />, title: 'Marketing' },
  { path: '/marketing/dashboard', element: <MarketingDashboard />, title: 'Marketing' },
  { path: '/marketing/listings', element: <Listings />, title: 'Marketing' },
  { path: '/marketing/listings/queue', element: <Listings />, title: 'Marketing' },
  { path: '/marketing/listing/:id', element: <Listing /> },
  { path: '/marketing/listing/:id/activity', element: <Listing /> },
  { path: '/marketing/listing/:id/changes', element: <Listing /> },
  { path: '/marketing/listing/:id/insights', element: <Listing /> },
  { path: '/marketing/listing/:id/notes', element: <Listing /> },
  { path: '/marketing/listing/:id/suggestions', element: <Listing /> },
  { path: '/marketing/coupons', element: <Coupons />, title: 'Marketing' },
  { path: '/marketing/coupon/:id', element: <Coupon /> },

  // Default redirect
  { path: '*', element: <Navigate to="/machines/atms" replace /> },
];

export const ROUTES_PERMISSION = [
  {
    path: '/sales/*',
    permission: 'sales.view',
  },
  {
    path: '/wallet/addresses',
    permission: 'wallets.generate_address',
  },
  {
    path: '/compliance',
    permission: 'kyc.view',
  },
  {
    path: '/invoices',
    permission: 'invoices.view',
  },
  {
    path: '/stats',
    permission: 'customer_statistics.view',
  },
  {
    path: '/users',
    permission: 'users.view',
  },
  {
    path: '/users-v2',
    permission: 'users.view',
  },
  {
    path: '/affiliates',
    permission: 'affiliates.view',
  },
  {
    path: '/affiliate/*',
    permission: 'affiliates.view',
  },
  {
    path: '/issues/*',
    permission: 'operations.view',
  },
  {
    path: '/cases/create',
    permission: 'cases.create',
  },
  {
    path: '/cases/*',
    permission: 'operations.view',
  },
  {
    path: '/service-ticket/create',
    permission: 'service_tickets.create',
  },
  {
    path: '/service-tickets/*',
    permission: 'operations.view',
  },
  {
    path: '/routes/*',
    permission: 'operations.view',
  },
  {
    path: '/merchants/*',
    permission: 'operations.view',
  },
  {
    path: '/configs/*',
    permission: 'operations.view',
  },
  {
    path: '/marketing/coupons',
    permission: 'coupons.view',
    operatorId: BITSTOP_OPERATOR_ID,
  },
  {
    path: '/marketing/coupon/:id',
    permission: 'coupons.view',
    operatorId: BITSTOP_OPERATOR_ID,
  },
  {
    path: '/marketing/*',
    permission: 'location_queue.view',
    operatorId: BITSTOP_OPERATOR_ID,
  },
];

export const ROUTES = [...SETTINGS_ROUTES, ...MAIN_ROUTES];

export const ROUTES_PATHS = ROUTES.map(({ path }) => path);
export const ROUTES_TITLE = ROUTES.map(({ title }) => title);
